import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import DrawerMenu from 'components/DrawerMenu';
import ScrollableTable from 'components/ScrollableTable';
import strings from 'common/Translation/Translate';
import React, { FC, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Config from 'utils/Config';
import { identifiers } from 'utils/constants/identifiers';
import { URLS } from 'utils/constants/urls';
import { fetchFromStorage } from 'utils/storage';
import { downloadExportFile } from 'utils/validators/HelperFunctions';

export type ImportValidationResult = {
  fileValid: boolean;
  sheetValid: boolean;
  headersValid: boolean;
  validRowCount: number;
  errorRowCount: number;
  readyToImport: boolean;
  fileErrors: string[];
  errorRows: Array<{ rowNumber: number; name: string; errors: string[] }>;
  itemsToCreate: Array<{ name: string; type?: string; category?: string }>;
};

type PropertyOnboardingImportDrawerProps = {
  open: boolean;
  file: File | null;
  title: string;
  validateUrl: string;
  importUrl: string;
  previewEmptyText: string;
  infoText?: string;
  onClose: () => void;
  onImportSuccess: () => void;
};

const ValidationCheck: FC<{ label: string; passed: boolean }> = ({ label, passed }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {passed ? (
      <CheckCircleOutlineIcon color="success" fontSize="small" />
    ) : (
      <CancelOutlinedIcon color="error" fontSize="small" />
    )}
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const PropertyOnboardingImportDrawer: FC<PropertyOnboardingImportDrawerProps> = ({
  open,
  file,
  title,
  validateUrl,
  importUrl,
  previewEmptyText,
  infoText,
  onClose,
  onImportSuccess,
}) => {
  const token = fetchFromStorage(identifiers.access_token);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validation, setValidation] = useState<ImportValidationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runValidation = useCallback(async () => {
    if (!file) return;

    setValidating(true);
    setValidation(null);
    setErrorMessage(null);

    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await axios.post<ImportValidationResult>(
        `${Config.BaseURL}${validateUrl}`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setValidation(data);
    } catch {
      setErrorMessage(strings.property_onboarding_addon_validate_error);
    } finally {
      setValidating(false);
    }
  }, [file, token, validateUrl]);

  useEffect(() => {
    if (open && file) {
      runValidation();
    }
    if (!open) {
      setValidation(null);
      setErrorMessage(null);
      setValidating(false);
      setImporting(false);
    }
  }, [open, file, runValidation]);

  const handleImport = async () => {
    if (!file || !validation?.readyToImport) return;

    setImporting(true);
    setErrorMessage(null);
    try {
      const body = new FormData();
      body.append('file', file);
      await axios({
        url: `${Config.BaseURL}${importUrl}`,
        method: 'POST',
        data: body,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      onImportSuccess();
      onClose();
    } catch (error: any) {
      if (error?.response?.status === 406) {
        downloadExportFile(error.response.data, 'xlsx', 'errorFile');
        setErrorMessage(strings.import_booking_error);
      } else {
        setErrorMessage(strings.property_onboarding_upload_error);
      }
    } finally {
      setImporting(false);
    }
  };

  const structureValid =
    validation?.fileValid && validation?.sheetValid && validation?.headersValid && !validation?.fileErrors?.length;
  const hasRowErrors = (validation?.errorRowCount ?? 0) > 0;
  const showPreview = structureValid && !hasRowErrors;

  return (
    <DrawerMenu
      open={open}
      handleClose={onClose}
      closeButton
      title={title}
      width="min(760px, 94vw)"
      flexColumn>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 2 }}>
        {infoText && (
          <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ py: 0.5 }}>
            {infoText}
          </Alert>
        )}

        {file && (
          <Typography variant="body2" color="text.secondary">
            {strings.property_onboarding_addon_import_file.replace('{file}', file.name)}
          </Typography>
        )}

        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {strings.property_onboarding_addon_validation_step}
          </Typography>

          {validating ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {strings.property_onboarding_addon_validating}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              <ValidationCheck
                label={strings.property_onboarding_addon_check_file}
                passed={Boolean(validation?.fileValid)}
              />
              <ValidationCheck
                label={strings.property_onboarding_addon_check_sheet}
                passed={Boolean(validation?.sheetValid)}
              />
              <ValidationCheck
                label={strings.property_onboarding_addon_check_headers}
                passed={Boolean(validation?.headersValid)}
              />
            </Stack>
          )}
        </Box>

        {validation?.fileErrors?.length ? (
          <Alert severity="error" sx={{ py: 0.5 }}>
            <Stack spacing={0.5}>
              {validation.fileErrors.map(err => (
                <Typography key={err} variant="body2">
                  {err}
                </Typography>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        {validation && !validating && (
          <>
            {hasRowErrors && (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon />}
                    label={strings.property_onboarding_addon_valid_count.replace(
                      '{count}',
                      String(validation.validRowCount),
                    )}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    icon={<CancelOutlinedIcon />}
                    label={strings.property_onboarding_addon_error_count.replace(
                      '{count}',
                      String(validation.errorRowCount),
                    )}
                    color="error"
                    variant="outlined"
                  />
                </Stack>

                <Box sx={{ minHeight: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {strings.property_onboarding_addon_error_grid_title}
                  </Typography>
                  <ScrollableTable
                    columns={[
                      { key: 'rowNumber', label: strings.property_onboarding_addon_error_row, align: 'center', width: 72 },
                      { key: 'name', label: 'Name', minWidth: 140 },
                      { key: 'errors', label: strings.property_onboarding_addon_error_message, minWidth: 220 },
                    ]}
                    data={validation.errorRows}
                    maxHeight={240}
                    getRowKey={(row, index) => `${row.rowNumber}-${index}`}
                    renderCell={(column, row) => (
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                        {column.key === 'errors'
                          ? row.errors?.join('; ') || '—'
                          : row[column.key ?? ''] ?? '—'}
                      </Typography>
                    )}
                    emptyMessage={strings.property_onboarding_addon_error_grid_empty}
                  />
                </Box>
              </>
            )}

            {showPreview && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {strings.property_onboarding_addon_preview_title}
                  </Typography>
                  {validation.itemsToCreate.length === 0 ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {previewEmptyText}
                    </Alert>
                  ) : (
                    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                      {validation.itemsToCreate.map((item, idx) => (
                        <Typography key={`${item.name}-${idx}`} variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {item.type ? `${item.name} (${item.type})` : item.name}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {strings.property_onboarding_addon_preview_total.replace(
                      '{count}',
                      String(validation.itemsToCreate.length),
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={importing}>
            {strings.cancel_text}
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!validation?.readyToImport || validating || importing}>
            {importing ? strings.property_onboarding_addon_importing : strings.property_onboarding_addon_import_confirm}
          </Button>
        </Box>
      </Box>
    </DrawerMenu>
  );
};

export default PropertyOnboardingImportDrawer;
